import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BedDouble, Plane, Car, MapPin, ChevronDown,
  LogOut, LayoutDashboard, Ticket,
} from 'lucide-react';
import './LandingNavbar.css';

const NAV_LINKS = [
  { label: 'Hotels',      to: '/booking',     Icon: BedDouble },
//   { label: 'Flights',     to: '/flights',     Icon: Plane     },
//   { label: 'Car Rental',  to: '/cars',        Icon: Car       },
  { label: 'Attractions', to: '/attractions', Icon: Ticket    },
];

export default function LandingNavbar() {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getDashboardPath = () => {
    const role = userProfile?.role;
    if (role === 'admin')   return '/admin';
    if (role === 'manager') return '/manager';
    if (role === 'owner')   return '/owner';
    return '/guest/dashboard';
  };

  const displayName =
    userProfile?.fullName ||
    userProfile?.full_name ||
    userProfile?.name ||
    currentUser?.email?.split('@')[0] ||
    'User';

  const initials = displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <nav className="ln-nav">
      <div className="ln-nav-inner">
        {/* ── Left: logo + nav tabs ── */}
        <div className="ln-left">
          <Link to="/" className="ln-logo">
            <span className="ln-logo-mark">I</span>nnBotics
          </Link>

          <div className="ln-tabs">
            {NAV_LINKS.map(({ label, to, Icon }) => {
              const active = location.pathname.startsWith(to);
              return (
                <Link
                  key={label}
                  to={to}
                  className={`ln-tab${active ? ' ln-tab-active' : ''}`}
                >
                  <Icon size={15} strokeWidth={2.2} />
                  <span>{label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* ── Right: auth controls ── */}
        <div className="ln-right">
          {currentUser ? (
            <div className="ln-avatar-wrap" ref={dropdownRef}>
              <button
                className="ln-avatar-btn"
                onClick={() => setDropdownOpen((v) => !v)}
              >
                <div className="ln-avatar-circle">{initials}</div>
                <span className="ln-avatar-name">{displayName}</span>
                <ChevronDown size={15} strokeWidth={2.5} />
              </button>

              {dropdownOpen && (
                <div className="ln-dropdown">
                  {/* Profile — guests only */}
                  {userProfile?.role === 'guest' && (
                    <button
                      className="ln-dropdown-item"
                      onClick={() => { navigate('/guest-profile'); setDropdownOpen(false); }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                      </svg>
                      Profile
                    </button>
                  )}

                  {/* My Bookings — guests only */}
                  {userProfile?.role === 'guest' && (
                    <button
                      className="ln-dropdown-item"
                      onClick={() => { navigate('/my-bookings'); setDropdownOpen(false); }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M8 2v4M16 2v4M3 10h18" />
                        <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
                      </svg>
                      My Bookings
                    </button>
                  )}

                  {/* Dashboard — all roles */}
                  <button
                    className="ln-dropdown-item"
                    onClick={() => {
                      navigate(getDashboardPath());
                      setDropdownOpen(false);
                    }}
                  >
                    <LayoutDashboard size={15} />
                    Dashboard
                  </button>

                  <div className="ln-dropdown-divider" />

                  <button
                    className="ln-dropdown-item ln-dropdown-item--danger"
                    onClick={async () => {
                      await logout();
                      navigate('/');
                      setDropdownOpen(false);
                    }}
                  >
                    <LogOut size={15} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button
                className="ln-btn-signin"
                onClick={() => navigate('/login')}
              >
                Sign in
              </button>
              <button
                className="ln-btn-register"
                onClick={() => navigate('/signup')}
              >
                Register
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
